import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from "react";
import { getLayersConfig } from "../../services/geoAdminService";
import * as ol from "openlayers";
import {
    clearVectorLayer,
    createTileLayer,
    createVectorLayers,
    getGeoLocation,
    navigateTo,
    onAddressFound,
    onMapClickHandler,
    onMapDoubleClickHandler,
    onMapMoveHandler,
} from "../../services/mapService";

import "./index.css";
import { defaultExtent } from "../../utils/map";
import type { IRoofAddress } from "../../react-app-env";
import { MapLegend } from "../MapLegend";

interface MapProps {
    roofDrafts: IRoofAddress[]
    i18nMap: any
    selectedDraft: IRoofAddress
    onClearRoofSelection: any
    onUpdateRoofAddress: any
    onSelectAllBuildingRoofs: any
    handleChangingTabs: any
    isChangingTabs: boolean
}

export const Map = forwardRef(({ roofDrafts, i18nMap, selectedDraft, onClearRoofSelection, onUpdateRoofAddress, onSelectAllBuildingRoofs, handleChangingTabs, isChangingTabs, }: MapProps, ref) => {

    const mapContainerElement = useRef<HTMLDivElement>(null);
    const mapElement = useRef<ol.Map | null>(null);
    const vectorBorderLayerRef = useRef<ol.layer.Vector | null>(null);
    const [solarLayerVisible, setSolarLayerVisible] = useState(false);
    const [hasZoomed, setHasZoomed] = useState(false);
    const [hintCollapsed, setHintCollapsed] = useState(false);
    const [hasInteracted, setHasInteracted] = useState(false);
    const isUserZoomRef = useRef(false);
    const addressFromClickRef = useRef(false);

    // Stable refs for callbacks to avoid re-triggering effects when props change
    const onUpdateRoofAddressRef = useRef(onUpdateRoofAddress);
    onUpdateRoofAddressRef.current = onUpdateRoofAddress;
    const onSelectAllBuildingRoofsRef = useRef(onSelectAllBuildingRoofs);
    onSelectAllBuildingRoofsRef.current = onSelectAllBuildingRoofs;

    // Reset flags when switching drafts (tabs)
    useEffect(() => {
        addressFromClickRef.current = false;
        setHasZoomed(false);
    }, [selectedDraft.id]);

    // map multi roof selection capability, default true
    const allowMultipleRoofs = true;

    const onUpdateRoofAddressHandler = useCallback((address: any, roof: any, searchAddress: any) => {
        onUpdateRoofAddressRef.current(address, roof, searchAddress, allowMultipleRoofs);
    }, []);

    const onClickUpdateHandler = useCallback((address: any, roof: any, searchAddress: any) => {
        addressFromClickRef.current = true;
        onUpdateRoofAddressRef.current(address, roof, searchAddress, allowMultipleRoofs);
    }, []);

    const onDoubleClickUpdateHandler = useCallback((address: any, roofs: any[], searchAddress: any) => {
        addressFromClickRef.current = true;
        onSelectAllBuildingRoofsRef.current(address, roofs, searchAddress);
    }, []);

    const initializeMap = useCallback(async () => {
        if (!mapContainerElement.current || mapElement.current) return;

        // Fetch layer configurations
        const layersConfig = await getLayersConfig(i18nMap.lang);

        // Initialize map layers
        const swissImageLayer = createTileLayer(
            "ch.swisstopo.swissimage",
            layersConfig
        );
        const solarEnergyLayer = createTileLayer(
            "ch.bfe.solarenergie-eignung-daecher",
            layersConfig,
            0.75
        );

        // Initialize projection and interactions
        const proj = initializeProjection();
        const interactions = initializeInteractions(false);

        const { borderLayer } = createVectorLayers();
        vectorBorderLayerRef.current = borderLayer;

        // Create map instance
        const map = new ol.Map({
            target: mapContainerElement.current!,
            layers: [swissImageLayer, solarEnergyLayer, borderLayer],
            view: new ol.View({
                extent: defaultExtent,
                resolutions: [
                    650, 500, 250, 100, 50, 20, 10, 5, 2.5, 2, 1, 0.5, 0.25, 0.1,
                ],
                center: ol.extent.getCenter(defaultExtent),
                projection: proj,
                zoom: 0,
            }),
            controls: ol.control.defaults({
                attributionOptions: { collapsible: false },
            }),
            logo: false,
            interactions: interactions,
        });

        // Add controls and event handlers
        map.addControl(new ol.control.ScaleLine());
        // Disable mousewheel zoom; keep DoubleClickZoom active for zoomed-out state
        map.getInteractions().forEach(function (interaction: { setActive: (arg0: boolean) => void; }) {
            if (interaction instanceof ol.interaction.MouseWheelZoom) { interaction.setActive(false); }
        });

        // Resolution threshold: at ≤ 0.3 m/px switch from zoom to roof selection
        const ROOF_VISIBLE_RESOLUTION = 0.3;

        // Toggle DoubleClickZoom based on resolution: enabled when zoomed out, disabled when roofs are visible
        const dblClickZoomInteraction = map.getInteractions().getArray()
            .find((i: any) => i instanceof ol.interaction.DoubleClickZoom);
        const updateDoubleClickZoom = () => {
            if (dblClickZoomInteraction) {
                const res = map.getView().getResolution();
                (dblClickZoomInteraction as any).setActive(res > ROOF_VISIBLE_RESOLUTION);
            }
        };
        map.getView().on("change:resolution", updateDoubleClickZoom);
        updateDoubleClickZoom();

        map.on("singleclick", (e: any) => {
            onMapClickHandler(
                e,
                mapElement.current as ol.Map,
                i18nMap.lang,
                onClickUpdateHandler
            );
        });
        map.on("dblclick", (e: any) => {
            // Only select all roofs when zoomed in enough to see them
            const res = map.getView().getResolution();
            if (res <= ROOF_VISIBLE_RESOLUTION) {
                onMapDoubleClickHandler(
                    e,
                    mapElement.current as ol.Map,
                    i18nMap.lang,
                    onDoubleClickUpdateHandler
                );
            }
        });
        map.on("pointermove", (e: any) =>
            onMapMoveHandler(e, mapElement.current as ol.Map, solarEnergyLayer)
        );

        map.on("moveend", () => {
            const view = map.getView();
            const center = view.getCenter();
            const resolution = view.getResolution();
            const mapSize = map.getSize();

            // Calculate half-dimensions of the visible area in map units
            const halfWidth = (mapSize[0] * resolution) / 2;
            const halfHeight = (mapSize[1] * resolution) / 2;

            // Compute the min and max allowed center coordinates
            const minX = defaultExtent[0] + halfWidth;
            const minY = defaultExtent[1] + halfHeight;
            const maxX = defaultExtent[2] - halfWidth;
            const maxY = defaultExtent[3] - halfHeight;

            // Clamp the center coordinates within the allowed range
            const clampedCenter = [
                Math.min(Math.max(center[0], minX), maxX),
                Math.min(Math.max(center[1], minY), maxY),
            ] as [number, number];


            // If the center is out of bounds, reset it
            if (clampedCenter[0] !== center[0] || clampedCenter[1] !== center[1]) {
                view.setCenter(clampedCenter);
            }
        });


        addCustomControls(map, i18nMap, () => onClearRoofSelection());

        // After adding controls and event handlers, before assigning mapElement.current
        // Add an event listener to enable/disable drag pan based on the zoom level
        const updateDragPanInteraction = () => {
            const view = map.getView();
            const currentZoom = view.getZoom();
            map.getInteractions().forEach((interaction) => {
                if (interaction instanceof ol.interaction.DragPan) {
                    // Disable drag pan at initial zoom (0) and enable otherwise
                    interaction.setActive(currentZoom !== 0);
                }
            });
        };

        // Show legend only when zoomed in enough to see colored roofs (resolution <= 50)
        const SOLAR_VISIBLE_MAX_RESOLUTION = 50;
        const updateSolarLayerVisibility = () => {
            const res = map.getView().getResolution();
            const visible = res <= SOLAR_VISIBLE_MAX_RESOLUTION;
            setSolarLayerVisible(prev => prev === visible ? prev : visible);
        };

        // Track user-initiated zoom (not programmatic)
        map.on("pointerdown", () => {
            isUserZoomRef.current = true;
            setHasInteracted(true);
        });
        map.getTargetElement()?.addEventListener("wheel", () => {
            isUserZoomRef.current = true;
            setHasInteracted(true);
        });

        // Listen to resolution (zoom) changes
        map.getView().on("change:resolution", () => {
            updateDragPanInteraction();
            updateSolarLayerVisibility();
            if (isUserZoomRef.current) {
                setHasZoomed(true);
            }
            // Reset after a short delay to distinguish user vs programmatic
            setTimeout(() => { isUserZoomRef.current = false; }, 100);
        });
        // Call initially
        updateDragPanInteraction();
        updateSolarLayerVisibility();

        mapElement.current = map;
        // default search
        if (selectedDraft.searchAddress?.cleanLabel) {
            onAddressFound(
                mapElement.current as any,
                selectedDraft.searchAddress,
                i18nMap.lang,
                onUpdateRoofAddressHandler
            );
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useImperativeHandle(ref, () => ({
        geoLocation() {
            getGeoLocation(mapElement.current as ol.Map, onUpdateRoofAddressHandler, (e: any) => console.error(e));
        }
    }));

    useEffect(() => {
        initializeMap();

        return () => {
            if (mapElement.current) {
                onUpdateRoofAddress(null, null, true)
            }
        };
    }, []);

    useEffect(() => {
        if (addressFromClickRef.current) {
            addressFromClickRef.current = false;
            return;
        }
        if (!selectedDraft.searchAddress?.cleanLabel) return;

        const tryZoom = () => {
            if (mapElement.current) {
                onAddressFound(
                    mapElement.current as any,
                    selectedDraft.searchAddress,
                    i18nMap.lang,
                    onUpdateRoofAddressHandler
                ).catch((err: any) => console.error("onAddressFound failed:", err));
            }
        };

        // If map not ready yet, retry once after short delay
        if (!mapElement.current) {
            const retryTimer = setTimeout(tryZoom, 300);
            return () => clearTimeout(retryTimer);
        }

        tryZoom();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedDraft.searchAddress]);

    // Track previous roof state to avoid redundant redraws and navigations
    const prevRoofsKeyRef = useRef<string>('');

    // adding markers and border to roof
    useEffect(() => {
        const borderLayer = vectorBorderLayerRef?.current;
        if (!borderLayer) return;

        const currentRoofsKey = selectedDraft.id + ':' + JSON.stringify(
            selectedDraft?.roofs?.map((r: any) => r?.geometry?.rings) ?? []
        );
        const roofsChanged = currentRoofsKey !== prevRoofsKeyRef.current;
        prevRoofsKeyRef.current = currentRoofsKey;

        if (!roofsChanged) return;

        clearVectorLayer(borderLayer);
        if (selectedDraft?.roofs?.length) {
            const polygonRings: ol.Coordinate[][][] = [];
            const allEdges: { v1: number[], v2: number[], key: string }[] = [];
            let lastCenter: number[] | null = null;

            selectedDraft.roofs.forEach((roof: any) => {
                if (roof?.geometry?.rings) {
                    polygonRings.push(roof.geometry.rings);
                    const polygon = new ol.geom.Polygon(roof.geometry.rings);
                    lastCenter = polygon.getInteriorPoint().getCoordinates();

                    // Collect edges for shared-edge detection
                    roof.geometry.rings.forEach((ring: number[][]) => {
                        for (let i = 0; i < ring.length - 1; i++) {
                            const v1 = ring[i];
                            const v2 = ring[i + 1];
                            const sorted = [`${v1[0]},${v1[1]}`, `${v2[0]},${v2[1]}`].sort();
                            allEdges.push({ v1, v2, key: sorted.join('-') });
                        }
                    });
                }
            });

            if (polygonRings.length > 0) {
                // Count edge occurrences — shared edges appear twice
                const edgeCounts: Record<string, number> = {};
                allEdges.forEach(e => {
                    edgeCounts[e.key] = (edgeCounts[e.key] || 0) + 1;
                });

                // Fill: MultiPolygon covering all selected roofs
                const multiPolygon = new ol.geom.MultiPolygon(polygonRings);
                const fillFeature = new ol.Feature(multiPolygon);
                fillFeature.setStyle(new ol.style.Style({
                    fill: new ol.style.Fill({
                        color: [76, 255, 41, 0.55],
                    }),
                }));
                borderLayer.getSource()?.addFeature(fillFeature);

                // Border: only outer edges (edges that appear once)
                const outerEdgeCoords = allEdges
                    .filter(e => edgeCounts[e.key] === 1)
                    .map(e => [e.v1, e.v2]);

                if (outerEdgeCoords.length > 0) {
                    const multiLine = new ol.geom.MultiLineString(outerEdgeCoords as ol.Coordinate[][]);
                    const borderFeature = new ol.Feature(multiLine);
                    borderFeature.setStyle([
                        new ol.style.Style({
                            stroke: new ol.style.Stroke({
                                color: [255, 255, 255, 0.7],
                                width: 5,
                            }),
                        }),
                        new ol.style.Style({
                            stroke: new ol.style.Stroke({
                                color: [15, 95, 41, 0.9],
                                width: 3,
                            }),
                        }),
                    ]);
                    borderLayer.getSource()?.addFeature(borderFeature);
                }
            }

            if (lastCenter && mapElement.current) {
                navigateTo(mapElement.current, lastCenter as [number, number], 0.25, isChangingTabs);
            }
            handleChangingTabs(false);
        }
    }, [selectedDraft]);

    const hasAddress = !!selectedDraft.searchAddress?.cleanLabel;
    const hasRoofs = selectedDraft.roofs && selectedDraft.roofs.length > 0;
    const isHidden = hasZoomed || hasInteracted || solarLayerVisible;
    const showAddressHint = !hasAddress && !isHidden;
    const showRoofHint = hasAddress && !hasRoofs && !isHidden;
    const showFlag = !hasAddress && !isHidden;

    return (
        <div style={{ width: "100%", height: "100%", position: "relative", overflow: "hidden" }}>
            <div ref={mapContainerElement} style={{ width: "100%", height: "100%" }} />
            {showAddressHint && (
                <div className="map-hint-overlay">
                    <div className="map-hint-text">
                        {i18nMap.enterAddressHint}
                    </div>
                </div>
            )}
            {showRoofHint && (
                <div className="map-hint-overlay">
                    {i18nMap.clickOn + " " + i18nMap.map}
                </div>
            )}
            {solarLayerVisible && <MapLegend i18nMap={i18nMap} />}
            {showFlag && (
                <div className="map-swiss-flag">
                    <svg className="swiss-flag-icon" viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg">
                        <rect width="32" height="32" rx="3" fill="#D52B1E" />
                        <rect x="6" y="13" width="20" height="6" rx="1" fill="#fff" />
                        <rect x="13" y="6" width="6" height="20" rx="1" fill="#fff" />
                    </svg>
                </div>
            )}
            {solarLayerVisible && (
                <div
                    className={`map-double-click-hint ${hintCollapsed ? 'map-hint--collapsed' : 'map-hint--expanded'}`}
                    onClick={() => setHintCollapsed(!hintCollapsed)}
                >
                    <span className="map-hint-icon">
                        <i className={hintCollapsed ? 'fa fa-info-circle' : 'fa fa-hand-pointer-o'} />
                    </span>
                    <span className="map-hint-text-inline">
                        {'ontouchstart' in window ? (i18nMap.doubleTapHint || i18nMap.doubleClickHint) : i18nMap.doubleClickHint}
                    </span>
                </div>
            )}
        </div>
    );
});

const initializeProjection = () => {
    const proj = ol.proj.get("EPSG:21781");
    proj.setExtent(defaultExtent);
    return proj;
};

const addCustomControls = (map: ol.Map, i18nMap: any, onMultiRoofClearHandler: any) => {
    // Add clear marker button
    const clearButton = document.createElement("button");
    clearButton.innerHTML = i18nMap.clearRoofSelectionLabel;
    clearButton.className = "clear-selection-button";
    clearButton.onclick = () => {
        onMultiRoofClearHandler();
    };
    const clearButtonControl = new ol.control.Control({ element: clearButton });
    map.addControl(clearButtonControl);
};

const initializeInteractions = (noInteraction: boolean) => {
    if (noInteraction) {
        return ol.interaction.defaults({
            altShiftDragRotate: false,
            doubleClickZoom: false,
            dragPan: false,
            pinchRotate: false,
            pinchZoom: false,
            keyboard: false,
            mouseWheelZoom: false,
            shiftDragZoom: false,
        });
    }
    return ol.interaction.defaults();
};